import { Check } from "lucide-react";
import { useQuestingGroups } from "../../../hooks/useQuestingGroups";

/**
 * Phase 3c-2 U2: multi-select chip picker over the user's questing groups.
 *
 * Selection semantics:
 *   - null = "global" (mark applies to all groups)
 *   - []  = same as null (no selected groups → global). Caller may receive
 *     `null` from us to flatten that case.
 *   - ["A", "B"] = applies only to those groups.
 *
 * Toggling the only selected chip returns null (back to global). Clicking an
 * unselected chip adds it. Clicking a selected chip removes it.
 */
export function QuestingGroupChipSelector({ selectedGroupIds, onChange }) {
  const { groups, loading, getGroupColor } = useQuestingGroups();
  const selected = Array.isArray(selectedGroupIds) ? selectedGroupIds : [];

  if (loading) {
    return (
      <div className="text-xs text-slate-500 dark:text-slate-400">
        Loading groups...
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="text-xs text-slate-500 dark:text-slate-400">
        You aren't in any questing groups yet. Marks apply to all polls by default.
      </div>
    );
  }

  const toggle = (groupId) => {
    const next = selected.includes(groupId)
      ? selected.filter((id) => id !== groupId)
      : [...selected, groupId];
    onChange?.(next.length === 0 ? null : next);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {groups.map((group) => {
        const isSelected = selected.includes(group.id);
        const baseColor =
          (typeof getGroupColor === "function" && getGroupColor(group.id)) ||
          group.color ||
          "#64748b";
        return (
          <button
            key={group.id}
            type="button"
            onClick={() => toggle(group.id)}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              isSelected
                ? "text-white"
                : "text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
            }`}
            style={{
              backgroundColor: isSelected ? baseColor : undefined,
              borderColor: baseColor,
            }}
            aria-pressed={isSelected}
          >
            {isSelected ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
            <span>{group.name || "Untitled group"}</span>
          </button>
        );
      })}
    </div>
  );
}
